class SuperHeader extends HTMLElement {
  constructor() {
    super();
    this.header = this;
    this.lastScrollTop = 0;
    this.ticking = false;
  }

  connectedCallback() {
    this.menuType = this.dataset.menuType;
    this.stickyBehavior = this.dataset.stickyBehavior;
    this.mobileMenuType = this.dataset.mobileMenuType;
    this.submenuAction = this.dataset.submenuAction;
    this.headerLower = this.querySelector('.header-lower');
    this.headerUpper = this.querySelector('.header-upper');
    this.setupStickyHeader();

    if (this.menuType === 'dropdown' || this.menuType === 'mega') {
      this.setupDesktopSubmenuBehavior();
    }

    this.setupMenuDrawer();
    this.setupPredictiveSearch();
    this.setupAnnouncementBarClose();
  }

  setupStickyHeader() {
    this.headerHeight = this.header.offsetHeight;
    this.headerUpperHeight = this.headerUpper.offsetHeight;
    this.headerLowerHeight = this.headerLower?.offsetHeight || 0;
    this.announcementBar = document.querySelector('.announcement-bar');
    this.announcementBarHeight = this.announcementBar ? this.announcementBar.offsetHeight : 0;
  
    document.documentElement.style.setProperty('--header-height', `${this.headerHeight}px`);
    document.documentElement.style.setProperty('--header-upper-height', `${this.headerUpperHeight}px`);
    document.documentElement.style.setProperty('--header-lower-height', `${this.headerLowerHeight}px`);
    document.documentElement.style.setProperty('--announcement-bar-height', `${this.announcementBarHeight}px`);
  
    if (this.stickyBehavior === 'always' || this.stickyBehavior === 'scroll_up') {
      window.addEventListener('scroll', this.onScroll.bind(this), false);
    }
  }

  onScroll() {
    if (!this.ticking) {
      window.requestAnimationFrame(() => {
        this.updateHeaderVisibility();
        this.ticking = false;
      });
      this.ticking = true;
    }
  }

  updateHeaderVisibility() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const isMobile = window.innerWidth < 1024; // lg breakpoint
    const headerBottom = this.header.getBoundingClientRect().bottom;
    const announcementBarBottom = this.announcementBar ? this.announcementBar.getBoundingClientRect().bottom : -1;

    if (isMobile) {
      // Mobile behavior - stick the header-upper
      if (scrollTop > this.announcementBarHeight) {
        this.headerUpper.classList.add('fixed', 'top-0', 'w-full', 'z-30', 'bg-background');
        this.header.style.paddingTop = `${this.headerUpperHeight}px`;
      } else {
        this.headerUpper.classList.remove('fixed', 'top-0', 'w-full', 'z-30', 'bg-background');
        this.header.style.paddingTop = '0';
      }
    } else {
      // Desktop behavior - stick the header-lower
      if (scrollTop > (this.headerUpperHeight + this.announcementBarHeight)) {
        this.headerLower.classList.add('fixed', 'top-0', 'w-full', 'z-30', 'bg-background');
        this.header.style.paddingTop = `${this.headerLowerHeight}px`;
      } else {
        this.headerLower.classList.remove('fixed', 'top-0', 'w-full', 'z-30', 'bg-background');
        this.header.style.paddingTop = '0';
      }
    }
  }

  setupDesktopSubmenuBehavior() {
    const menuItems = this.header.querySelectorAll('nav > ul > li');

    menuItems.forEach(item => {
      const submenu = item.querySelector('ul') || item.querySelector('.mega-menu');
      if (submenu) {
        if (this.submenuAction === 'hover') {
          item.addEventListener('mouseenter', () => {
            item.classList.add('active-menu-link');
          });
          item.addEventListener('mouseleave', () => {
            item.classList.remove('active-menu-link');
          });
        } else if (this.submenuAction === 'click') {
          item.querySelector('a').addEventListener('click', (e) => {
            e.preventDefault();
            item.classList.toggle('active-menu-link');
            menuItems.forEach(otherItem => {
              if (otherItem !== item) {
                otherItem.classList.remove('active-menu-link');
              }
            });
          });

          document.addEventListener('click', (e) => {
            if (!item.contains(e.target)) {
              item.classList.remove('active-menu-link');
            }
          });
        }
      }
    });
  }

  setupMenuDrawer() {
    const menuToggle = this.header.querySelector('#menu-toggle');
    const menuDrawer = this.header.querySelector('#menu-drawer');
    const menuIcon = menuToggle.querySelector('.menu-icon');
    const closeIcon = menuToggle.querySelector('.close-icon');
    const overlay = this.header.querySelector('#drawer-overlay');
    const menuToggleOpen = this.header.querySelector('#menu-toggle-open');


    this.setDrawerHeight();
    window.addEventListener('resize', () => {
      this.setDrawerHeight();
      this.setupStickyHeader();
    });
    window.addEventListener('scroll', this.setDrawerHeight.bind(this));

    menuToggleOpen.addEventListener('click', () => {
      this.toggleDrawer(menuDrawer, menuIcon, closeIcon, overlay);
    });

    menuToggle.addEventListener('click', () => {
      this.toggleDrawer(menuDrawer, menuIcon, closeIcon, overlay);
    });

    overlay.addEventListener('click', () => {
      this.toggleDrawer(menuDrawer, menuIcon, closeIcon, overlay);
    });

    if (this.mobileMenuType !== 'accordion') {
      this.setupNewPageInDrawer(menuDrawer);
    }
  }

  setDrawerHeight() {
    const headerHeight = this.headerUpper.offsetHeight;
    const announcementBar = document.querySelector('.announcement-bar');
    let announcementBarVisibleHeight = 0;
  
    if (announcementBar) {
      const announcementBarRect = announcementBar.getBoundingClientRect();
      announcementBarVisibleHeight = Math.max(0, announcementBarRect.height - Math.abs(announcementBarRect.top));
    }
  
    const totalHeight = 0;
    const menuDrawer = this.header.querySelector('#menu-drawer');
    const menuDrawerOverlay = this.header.querySelector('#drawer-overlay');
    menuDrawer.style.height = `calc(100dvh - ${totalHeight}px)`;
    menuDrawer.style.top = `${totalHeight}px`;
    menuDrawerOverlay.style.height = `calc(100dvh - ${totalHeight}px)`;
    menuDrawerOverlay.style.top = `${totalHeight}px`;
  }

  toggleDrawer(menuDrawer, menuIcon, closeIcon, overlay) {
    menuDrawer.classList.toggle('-translate-x-full');
    menuIcon.classList.toggle('hidden');
    closeIcon.classList.toggle('hidden');
    document.body.classList.toggle('overflow-hidden');
    overlay.classList.toggle('hidden');
  }

  setupNewPageInDrawer(menuDrawer) {
    const submenuTriggers = menuDrawer.querySelectorAll('.submenu-trigger');
    submenuTriggers.forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        const submenuId = e.target.dataset.submenu;
        const submenu = this.header.querySelector(`#submenu-${submenuId}`);
        submenu.classList.remove('hidden');
      });
    });

    const submenuBackButtons = menuDrawer.querySelectorAll('.submenu-back');
    submenuBackButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.target.closest('.submenu').classList.add('hidden');
      });
    });
  }

  setupPredictiveSearch() {
    
    const searchInput = this.header.querySelector('#search-input');
    const searchResults = this.header.querySelector('#search-results');
    const resultsWrappers = this.header.querySelectorAll('.results-wrapper');
    
    
    const searchToggle = this.header.querySelector('#search-toggle');
    const predictiveSearch = this.header.querySelector('#predictive-search');
    const searchClose = this.header.querySelector('#search-close');
    const searchClear = this.header.querySelector('#search-clear');
    
    
    document.addEventListener('click', (e) => {
      const searchContainers = this.header.querySelectorAll('.main-searchbar');
      let clickedOutside = true;
      
      searchContainers.forEach(container => {
        if (container && container.contains(e.target)) {
          clickedOutside = false;
        }
      });
      
      if (clickedOutside) {
        resultsWrappers.forEach(wrapper => {
          if (!wrapper.classList.contains('hidden')) {
            wrapper.classList.add('hidden');
          }
        });
      }
    });
    
    
    searchInput?.addEventListener('input', () => {
      if (searchInput.value.trim() === '') {
        resultsWrappers.forEach(wrapper => {
          wrapper.classList.add('hidden');
        });
      }
    });
    
    
    searchToggle?.addEventListener('click', () => {
      predictiveSearch?.classList.remove('hidden');
      document.body.classList.add('overflow-hidden');
      searchInput?.focus();
    });
    
   
    searchClose?.addEventListener('click', () => {
      predictiveSearch?.classList.add('hidden');
      if (searchInput) searchInput.value = '';
      if (searchResults) searchResults.innerHTML = '';
      document.body.classList.remove('overflow-hidden');
    });
    
    searchClear?.addEventListener('click', () => {
      if (searchInput) {
        searchInput.value = '';
        searchResults.innerHTML = '';
        searchClear.classList.add('hidden');
        searchInput.focus();
        
        resultsWrappers.forEach(wrapper => {
          wrapper.classList.add('hidden');
        });
      }
    });
    const debounce = (func, wait) => {
      let timeout;
      return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => func.apply(this, args), wait);
      };
    }
    const searchInputs = this.header.querySelectorAll('.search-input');
    searchInputs.forEach(input => {
      input?.addEventListener('input', debounce(async (e) => {
        const query = e.target.value.trim();
      
        if (query.length > 1) {
          searchClear?.classList.remove('hidden');
      
          try {
            const response = await fetch(`/search/suggest?q=${query}&section_id=predictive-search`);
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }
    
            const text = await response.text();
            const parser = new DOMParser();
            const parsedDocument = parser.parseFromString(text, 'text/html');
    
            resultsWrappers.forEach(wrapper => {
              wrapper.classList.remove('hidden');
              if (wrapper.classList.contains('flex') || window.innerWidth >= 1024) {
                wrapper.classList.add('flex');
              }
            });
      
            const resultsMarkup = parsedDocument.querySelector('#predictive-search-results')?.innerHTML || '';
            const searchResultsElements = this.header.querySelectorAll('#search-results');
            searchResultsElements.forEach(element => {
              element.innerHTML = resultsMarkup;
            });
    
          } catch (error) {
            console.error('Error fetching search results:', error);
          }
        } else {
          if (searchResults) searchResults.innerHTML = '';
          resultsWrappers.forEach(wrapper => {
            wrapper.classList.add('hidden');
          });
          searchClear?.classList.add('hidden');
        }
      }, 300));
    });
  }
  setupAnnouncementBarClose() {
    const announcementCloseButton = this.announcementBar?.querySelector('#announcement-close');
    if (announcementCloseButton) {
      announcementCloseButton.addEventListener('click', () => {
        this.announcementBar.classList.add('hidden');
        this.announcementBar.classList.remove('announcement-bar');
        this.setDrawerHeight();
        this.setupStickyHeader();
      });
    }
  }
}

customElements.define('header-487', SuperHeader);